import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {string | undefined} */
  #pendingSelectedOptionId;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    this.selectVariantFromUrl();
    this.ensureAvailableVariantSelected();
  }
  
  selectVariantFromUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');
    
    if (!variantIdFromUrl) return;
    
    const alreadySelectedInput = this.querySelector(`input[data-variant-id="${variantIdFromUrl}"]:checked`);
    const alreadySelectedOption = this.querySelector(`option[data-variant-id="${variantIdFromUrl}"][selected]`);
    
    if (alreadySelectedInput || alreadySelectedOption) {
      const productForm = this.closest('product-form-component');
      if (productForm) {
        const variantIdInput = productForm.querySelector('input[name="id"][ref="variantId"]');
        if (!variantIdInput || !(variantIdInput instanceof HTMLInputElement) || !variantIdInput.value || variantIdInput.value === '') {
          if (alreadySelectedInput) {
            const changeEvent = new Event('change', { bubbles: true });
            Object.defineProperty(changeEvent, 'target', { value: alreadySelectedInput, enumerable: true });
            this.variantChanged(changeEvent);
          } else if (alreadySelectedOption) {
            const selectElement = alreadySelectedOption.closest('select');
            if (selectElement) {
              const changeEvent = new Event('change', { bubbles: true });
              Object.defineProperty(changeEvent, 'target', { value: selectElement, enumerable: true });
              this.variantChanged(changeEvent);
            }
          }
        }
      }
      return;
    }
    
    const variantInput = this.querySelector(`input[data-variant-id="${variantIdFromUrl}"]`);
    const variantOption = this.querySelector(`option[data-variant-id="${variantIdFromUrl}"]`);
    
    if (variantInput instanceof HTMLInputElement) {
      if (variantInput.dataset.optionAvailable === 'true' && !variantInput.disabled) {
        variantInput.checked = true;
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', { value: variantInput, enumerable: true });
        this.variantChanged(changeEvent);
      }
    } else if (variantOption instanceof HTMLOptionElement) {
      const selectElement = variantOption.closest('select');
      if (selectElement instanceof HTMLSelectElement && variantOption.dataset.optionAvailable === 'true' && !variantOption.disabled) {
        selectElement.value = variantOption.value;
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', { value: selectElement, enumerable: true });
        this.variantChanged(changeEvent);
      }
    }
  }
  
  ensureAvailableVariantSelected() {
    const fieldsets = this.querySelectorAll('fieldset');
    const selects = this.querySelectorAll('select');
    let hasChanged = false;
    
    fieldsets.forEach((fieldset) => {
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement) {
        const isAvailable = checkedInput.dataset.optionAvailable === 'true';
        if (!isAvailable) {
          const availableInput = fieldset.querySelector('input[data-option-available="true"]:not([disabled])');
          if (availableInput instanceof HTMLInputElement && availableInput !== checkedInput) {
            availableInput.checked = true;
            checkedInput.checked = false;
            hasChanged = true;
          }
        }
      }
    });
    
    selects.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      if (selectedOption) {
        const isAvailable = selectedOption.dataset.optionAvailable === 'true' && !selectedOption.disabled;
        if (!isAvailable) {
          const availableOption = Array.from(select.options).find(
            (option) => option.dataset.optionAvailable === 'true' && !option.disabled
          );
          if (availableOption && availableOption !== selectedOption) {
            select.value = availableOption.value;
            hasChanged = true;
          }
        }
      }
    });
    
    if (hasChanged) {
      const firstChangedInput = this.querySelector('input:checked[data-option-available="true"]');
      const firstChangedSelect = this.querySelector('select');
      
      if (firstChangedInput instanceof HTMLInputElement) {
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', { value: firstChangedInput, enumerable: true });
        this.variantChanged(changeEvent);
      } else if (firstChangedSelect instanceof HTMLSelectElement) {
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', { value: firstChangedSelect, enumerable: true });
        this.variantChanged(changeEvent);
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    const selectedOptionId = selectedOption.dataset.optionValueId ?? '';
    
    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOptionId }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.#pendingSelectedOptionId = selectedOptionId;
    
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    // Store the selected option ID before morphing, as it might be lost during DOM morphing
    const selectedOptionIdBeforeMorph = this.selectedOptionId;

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          const optionIdToRestore = this.#pendingSelectedOptionId || selectedOptionIdBeforeMorph;
          if (optionIdToRestore) {
            const selectedOptionElement = this.querySelector(`[data-option-value-id="${optionIdToRestore}"]`);
            if (selectedOptionElement) {
              this.updateSelectedOption(selectedOptionElement);
            }
          }

          const selectedOptionId = optionIdToRestore || this.selectedOptionId;
          this.#pendingSelectedOptionId = undefined;
          
          if (selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
